import {ChronologicalCommentsSort} from '@github-ui/commenting/useCommentsSortPreference'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback, useMemo, useState} from 'react'

import {type ActivityFilterCategory, itemTypesForActivityFilters, normalizeActivityFilters} from './activity-filters'
import {
  type ActivitySort,
  NO_ACTIVITY_FILTERS,
  useActivityFilters,
  useActivitySort,
} from './use-activity-timeline-preferences'

/**
 * Everything the Activity panel needs to filter and sort its timeline.
 *
 * Activity follows the comments sort until the reader picks an Activity sort, so the two don't open in
 * opposite orders.
 *
 * A permalinked event may be one the saved filters hide, and its type isn't known from the URL. So
 * while the panel shows a permalink, the saved filters are set aside until the reader asks for them
 * back or changes them.
 */
export function useActivityTimelineView({
  enabled,
  highlightedEvent,
  commentsSort,
}: {
  /** Off for crawler pages and when the timeline is hidden: no filters, oldest first. */
  enabled: boolean
  highlightedEvent: string | undefined
  commentsSort: ActivitySort
}) {
  const [savedFilters, setSavedFilters] = useActivityFilters()
  const [savedSort, setSavedSort] = useActivitySort()
  const [filtersRestoredFor, setFiltersRestoredFor] = useState<string>()

  const filtersSetAside =
    enabled && savedFilters.length > 0 && highlightedEvent !== undefined && filtersRestoredFor !== highlightedEvent
  // The menu always shows the saved selection; the timeline reads it unless it is set aside.
  const filters = enabled ? savedFilters : NO_ACTIVITY_FILTERS
  const appliedFilters = filtersSetAside ? NO_ACTIVITY_FILTERS : filters
  const itemTypes = useMemo(() => itemTypesForActivityFilters(appliedFilters), [appliedFilters])
  const sort: ActivitySort = enabled ? (savedSort ?? commentsSort) : ChronologicalCommentsSort.OldestFirst

  const restoreFilters = useCallback(() => setFiltersRestoredFor(highlightedEvent), [highlightedEvent])

  const setFilters = useCallback(
    (next: readonly ActivityFilterCategory[]) => {
      const normalized = normalizeActivityFilters(next)
      setSavedFilters(normalized)
      // Changing filters from the menu is asking for them, permalink or not.
      setFiltersRestoredFor(highlightedEvent)
      sendEvent('issue_viewer.activity_panel.filter_changed', {
        filters: normalized.join(','),
        filter_count: normalized.length,
      })
    },
    [highlightedEvent, setSavedFilters],
  )

  const setSort = useCallback(
    (next: ActivitySort) => {
      setSavedSort(next)
      sendEvent('issue_viewer.activity_panel.sort_changed', {sort: next})
    },
    [setSavedSort],
  )

  // The permalink window is read unfiltered, so a filtered timeline goes without it.
  const timelineHighlightedEvent = itemTypes ? undefined : highlightedEvent

  return {filters, setFilters, itemTypes, timelineHighlightedEvent, filtersSetAside, restoreFilters, sort, setSort}
}
